import dayjs from "dayjs";
import { CalendarDays } from "lucide-react-native";
import { useState } from "react";
import DatePickerFormModal from "../../../../../forms/popoverDate/PickerWrapper";
import { Box } from "../../../../../ui/box";
import { Button, ButtonIcon, ButtonText } from "../../../../../ui/button";
import { HStack } from "../../../../../ui/hstack";
import { VStack } from "../../../../../ui/vstack";
import { isValidDate, readableDate } from "../../../wire";
import type { FieldControlProps } from "../fieldTypes";

/**
 * A day, picked from a calendar that opens under the button. Picker-only: a typed ISO
 * date is a way to get the format wrong.
 *
 * Inline rather than in a popover: the chat scrolls itself to the bottom while the
 * script streams — exactly when the Logistics card is being filled — and a popover
 * anchored inside a scrolling view closed or drifted off its trigger, so pressing the
 * button seemed to do nothing. In the flow of the card it just pushes the content down.
 * The calendar box needs its own width: the web picker lays itself out in percentages
 * (days 100%, cells 14.28%), so inside the shrink-wrapping `items-start` stack it
 * collapsed to nothing — the popover used to supply a width.
 *
 * Tapping a day is already a deliberate act, so it confirms and closes on its own. An
 * optional date can be cleared again — without that, choosing one by accident would be
 * irreversible on a field the person meant to leave empty.
 */
const DateField = ({ field, value, onChange, disabled }: FieldControlProps) => {
  const [open, setOpen] = useState(false);
  const text = typeof value === "string" ? value : "";
  const valid = isValidDate(text);
  const lowest = field.minDate
    ? dayjs(field.minDate).startOf("day").toDate()
    : dayjs().startOf("day").toDate();
  const highest = field.maxDate
    ? dayjs(field.maxDate).endOf("day").toDate()
    : undefined;

  return (
    <VStack space="xs" className="items-start">
      <Button
        action="muted"
        variant="outline"
        size="md"
        className="min-h-11 justify-start"
        isDisabled={disabled}
        aria-expanded={open}
        accessibilityLabel={
          valid
            ? `${field.label}: ${readableDate(text)}. Cambiar fecha`
            : `${field.label}: elegir fecha`
        }
        onPress={() => setOpen((current) => !current)}
      >
        <ButtonIcon as={CalendarDays} />
        <ButtonText>{valid ? readableDate(text) : "Elegir fecha"}</ButtonText>
      </Button>
      {open && !disabled ? (
        <Box className="w-80 max-w-full rounded-lg border border-outline-200 bg-background-0 p-2">
          <DatePickerFormModal
            date={valid ? dayjs(text).toDate() : undefined}
            onConfirm={(date) => {
              setOpen(false);
              onChange(dayjs(date).format("YYYY-MM-DD"));
            }}
            onBlur={() => {}}
            minDate={lowest}
            maxDate={highest}
          />
        </Box>
      ) : null}
      {!field.required && valid ? (
        <HStack>
          <Button
            action="muted"
            variant="link"
            size="xs"
            className="min-h-11"
            isDisabled={disabled}
            onPress={() => onChange(undefined)}
          >
            <ButtonText>Quitar fecha</ButtonText>
          </Button>
        </HStack>
      ) : null}
    </VStack>
  );
};

export default DateField;
